import { zodResolver } from '@hookform/resolvers/zod';
import { ApiClientError } from '@recipes/api-client';
import { loginRequestSchema, type LoginRequest } from '@recipes/contracts';
import { Alert, Button, Input } from '@recipes/ui';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router';
import { FormField } from '@/components/forms/form-field';
import {
  AuthModeLoading,
  IdentityErrorAlert,
  IdentitySignInButton,
  useAuthMode,
} from '@/features/auth/identity';
import { PasswordField } from '@/features/profile/password-field';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth/auth-provider';
import { describeError } from '@/lib/errors';
import { AuthLayout } from './auth-layout';

export function LoginPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const [params] = useSearchParams();
  const from = (location.state as { from?: string } | null)?.from ?? '/';
  const { mode, identity } = useAuthMode();

  if (mode === undefined) return <AuthModeLoading />;
  if (mode === 'oidc' && identity) {
    // The identity service signs users in (docs/partial/identity-service-spec.md 5.1).
    return (
      <AuthLayout
        title={t('auth.login')}
        description={t('auth.identity.description')}
        testId="login-page"
      >
        <div className="space-y-4">
          <IdentityErrorAlert error={params.get('error')} />
          <IdentitySignInButton identity={identity} returnTo={from} />
          <p className="text-center text-sm text-muted-foreground">
            {t('auth.identity.registerHint')}
          </p>
        </div>
      </AuthLayout>
    );
  }
  return <LocalLoginPage from={from} />;
}

function LocalLoginPage({ from }: { from: string }) {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  const [unverified, setUnverified] = useState(false);
  const [resent, setResent] = useState(false);

  const form = useForm<LoginRequest>({
    resolver: zodResolver(loginRequestSchema),
    // Coming from a confirmation link opened without a session: the address is known.
    defaultValues: {
      email: (location.state as { email?: string } | null)?.email ?? '',
      password: '',
    },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    setError(null);
    setUnverified(false);
    setResent(false);
    try {
      await login({ ...values, deviceName: navigator.userAgent.slice(0, 120) });
      navigate(from, { replace: true });
    } catch (err) {
      if (err instanceof ApiClientError && err.code === 'INVALID_CREDENTIALS')
        setError(t('auth.invalidCredentials'));
      else if (err instanceof ApiClientError && err.code === 'EMAIL_NOT_VERIFIED')
        setUnverified(true);
      else setError(describeError(err));
    }
  });

  return (
    <AuthLayout
      title={t('auth.login')}
      testId="login-page"
      footer={
        <>
          {t('auth.noAccount')}{' '}
          <Link
            to="/register"
            state={{ from }}
            className="font-medium text-primary underline-offset-4 hover:underline"
          >
            {t('auth.register')}
          </Link>
        </>
      }
    >
      <form
        onSubmit={onSubmit}
        noValidate
        className="space-y-4"
        aria-describedby={error ? 'login-error' : undefined}
      >
        {error && (
          <Alert variant="destructive" id="login-error" data-testid="login-error">
            {error}
          </Alert>
        )}
        {unverified && (
          <Alert variant="warning" data-testid="login-unverified">
            <p>{t('auth.verifyEmailHint')}</p>
            {resent ? (
              <p className="mt-2" data-testid="login-resent">
                {t('auth.verifyResent')}
              </p>
            ) : (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={() => {
                  setResent(true);
                  void api.auth
                    .resendVerificationEmail({ email: form.getValues('email') })
                    .catch(() => undefined);
                }}
                data-testid="login-resend"
              >
                {t('auth.verifyResend')}
              </Button>
            )}
          </Alert>
        )}
        <FormField
          label={t('auth.email')}
          error={form.formState.errors.email?.message && t('auth.errors.email')}
          required
        >
          <Input
            type="email"
            autoComplete="email"
            inputMode="email"
            data-testid="login-email"
            {...form.register('email')}
          />
        </FormField>
        <FormField
          label={t('auth.password')}
          error={form.formState.errors.password?.message && t('auth.errors.passwordRequired')}
          required
        >
          <PasswordField
            autoComplete="current-password"
            data-testid="login-password"
            {...form.register('password')}
          />
        </FormField>
        <div className="text-right text-sm">
          <Link to="/forgot-password" className="text-primary underline-offset-4 hover:underline">
            {t('auth.forgotPassword')}
          </Link>
        </div>
        <Button
          type="submit"
          className="w-full"
          loading={form.formState.isSubmitting}
          data-testid="login-submit"
        >
          {t('auth.login')}
        </Button>
      </form>
    </AuthLayout>
  );
}
